import { useEffect, useState, useCallback } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaginationState } from '@tanstack/react-table';
import { useSearchParams } from 'react-router-dom';

import { PersonsEditorTable } from './PersonsEditorTable';
import { PersonsEditorToolbar } from './PersonsEditorToolbar';
import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '../../../../../../../backend/src/types/types';
import { SubToolbar } from '../components/TableToolbar/SubToolbar';
import { TableWrapper } from '../components/TableWrapper';
import { PageName } from '@/analytics/events';
import { TrackingEvents } from '@/analytics/events';
import { trackPageView } from '@/analytics/Mixpanel';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { usePersonsDownload } from '@/api/mutations/useDownloadPersons';
import { useGetPersons } from '@/api/queries/useGetPersons';
import { DownloadModal } from '@/components/molecules/DownloadModal';
import { PersonFilters } from '@/types';

const PersonsEditor = () => {
  const [searchQueryInput, setSearchQueryInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const [filters, setFilters] = useState<PersonFilters>({
    personType: [],
  });

  const { data: personsResponse, isLoading } = useGetPersons({
    caseId,
    searchQuery,
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    paginated: true,
    filters,
  });
  const { mutateAsync: downloadPersons, isLoading: isLoadingDownload } = usePersonsDownload({
    caseId,
  });

  const handleDownloadCSV = useCallback(
    async ({
      title,
      columns,
      downloadFormat,
    }: {
      title: string;
      columns: string[];
      downloadFormat: DownloadFormat;
    }) => {
      if (downloadFormat === DownloadFormat.EXCEL) {
        trackCustomEvent(TrackingEvents.DownloadPersonsTableCSV, { caseId });
      } else if (downloadFormat === DownloadFormat.DOCX) {
        trackCustomEvent(TrackingEvents.DownloadPersonsTableDocx, { caseId });
      }

      await downloadPersons({ title, columns, styleFormat: StyleFormat.Table, downloadFormat });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, caseId],
  );

  const handleDownloadWord = useCallback(
    async ({
      title,
      downloadFormat,
      documentOptions,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      documentOptions: DocumentDownloadOptions;
    }) => {
      trackCustomEvent(TrackingEvents.DownloadPersonsWrittenDocx, { caseId });

      await downloadPersons({ title, styleFormat: StyleFormat.Written, downloadFormat, documentOptions });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, caseId],
  );

  const onSearchCall = async () => {
    setSearchQuery(searchQueryInput);
  };

  const clearSearch = async () => {
    setSearchQueryInput('');
    setSearchQuery('');
    await new Promise((resolve) => setTimeout(resolve, 0));
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pagination.pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pagination.pageIndex - 1);
  const nextPage = () => goToPage(pagination.pageIndex + 1);

  useEffect(() => {
    trackPageView(PageName.Entities);
  }, []);

  return (
    <div className="flex h-full min-w-full flex-col">
      <PersonsEditorToolbar
        activeFilters={filters}
        setFilters={setFilters}
        globalFilter={searchQueryInput}
        clearSearch={clearSearch}
        setGlobalFilter={setSearchQueryInput}
        onSearchCall={onSearchCall}
        currentPage={pagination.pageIndex}
        totalPages={personsResponse?.pageCount || 0}
        prevPage={prevPage}
        nextPage={nextPage}
        goToPage={goToPage}
        openDownloadModal={() => setDownloadModalIsOpen(true)}
      />
      <SubToolbar
        entityType="entities"
        totalCount={personsResponse?.totalCount}
        totalUnfilteredCount={personsResponse?.totalUnfilteredCount}
      />

      <TableWrapper>
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <FontAwesomeIcon icon={faCircleNotch} className="fa-spin" />
          </div>
        ) : (
          <div className="h-full w-full overflow-y-auto">
            <PersonsEditorTable persons={personsResponse?.persons || []} />
          </div>
        )}
      </TableWrapper>

      <DownloadModal
        modalTitle="Export Persons"
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        downloadTitle={'Persons'}
        isLoading={isLoadingDownload}
        columnOptions={personsDownloadColumnOptions}
        onDownloadTable={handleDownloadCSV}
        onDownloadWritten={handleDownloadWord}
        displayOptions={{ showStyle: false, showDocumentOptions: true }}
      />
    </div>
  );
};

export default PersonsEditor;

const personsDownloadColumnOptions = [
  { label: 'Name', value: 'Name' },
  { label: 'Type', value: 'Type' },
  { label: 'Aliases', value: 'Aliases' },
  { label: 'Description', value: 'Description' },
];
